import { map, type OperatorFunction } from 'rxjs';
import { AppError } from '../error/app-error';
import type { IPaginatedComposite, IResponse } from '../models/api-response.model';

/**
 * Unwraps the standard `IResponse<T>` envelope into a bare `T`.
 *
 * A non-2xx HTTP response is already converted to an `AppError` upstream by the
 * error interceptor, so this only guards the rare "200 but result:false" case —
 * and it throws an `AppError` (not a bare `Error`) so callers get a consistent
 * error type with `code`/`statusCode` populated.
 */
export function unwrap<T>(): OperatorFunction<IResponse<T>, T> {
  return map((res: IResponse<T>) => {
    if (!res.result || res.data === undefined) {
      throw new AppError(res.errorCode ?? 'UNKNOWN', res.statusMessage ?? '', res.statusCode ?? 0);
    }
    return res.data;
  });
}

/**
 * Unwraps a paginated envelope into `{ data, pagination }`.
 *
 * `pagination` is a top-level sibling of `data` on the wire (`IResponse.pagination`),
 * never nested inside `data` — so `unwrap()` would drop it.
 */
export function unwrapPaginated<T>(): OperatorFunction<IResponse<T>, IPaginatedComposite<T>> {
  return map((res: IResponse<T>) => {
    if (!res.result || res.data === undefined) {
      throw new AppError(res.errorCode ?? 'UNKNOWN', res.statusMessage ?? '', res.statusCode ?? 0);
    }
    if (!res.pagination) {
      throw new AppError(res.errorCode ?? 'UNKNOWN', res.statusMessage ?? '', res.statusCode ?? 0);
    }
    return { data: res.data, pagination: res.pagination };
  });
}
